import { Affix, Box, Button, Menu, useComputedColorScheme } from "@mantine/core";
import { IconChevronDown, IconDownload, IconPrinter } from "@tabler/icons-react";
import { createContext, useContext, useEffect, useRef, useState } from "react";

import Divider from "./PrintableDivider";
import Section from "./PrintableSection";
import { usePrintable } from "@hooks";
import { useTranslation } from "react-i18next";

export const A4_WIDTH_PX = 1406;
export const A4_HEIGHT_PX = 1987;
export const PRINTABLE_ROOT_ID = "printable-root";

const A4_LANDSCAPE_PAGE_WIDTH_PX = 1122;
const A4_LANDSCAPE_PAGE_HEIGHT_PX = 793;

const PrintableContext = createContext<{
  prettier: boolean;
  wrapperRef: React.RefObject<HTMLDivElement>;
}>({ prettier: true, wrapperRef: { current: null } });

export const usePrintableContext = () => {
  const context = useContext(PrintableContext);
  if (!context) {
    throw new Error("usePrintableContext must be used within a Printable component");
  }
  return context;
};
interface RootProps {
  children: React.ReactNode;
  prettier?: boolean;
  showPrintButton?: boolean;
  wide?: boolean;
  printWidth?: number;
  filename?: string;
  /**
   * When set, "Download" and "Print" route through the backend-driven render
   * endpoint (POST /pdf/test/:uuid) instead of building a self-contained HTML
   * blob client-side. Pass the test UUID(s) currently being shown — a single
   * UUID, a comma-separated string, or an array. Omit to keep the legacy
   * client-side flow.
   */
  testUuids?: string[] | string | null;
  sessionId?: string | null;
  /**
   * Query flags forwarded to the backend's print route so it reproduces the
   * same view (summary/vision/lazyeye/etc.). Maps 1:1 to /test-result params.
   */
  queryFlags?: Record<string, string | number | boolean | null | undefined>;
}

function Root({
  children,
  prettier = true,
  showPrintButton,
  wide = false,
  printWidth,
  filename = "report.pdf",
  testUuids,
  sessionId,
  queryFlags,
}: RootProps) {
  const printButtonVisible = showPrintButton ?? prettier;
  const printableWidth = wide ? A4_HEIGHT_PX : A4_WIDTH_PX;
  const printableMinHeight = wide ? A4_WIDTH_PX : A4_HEIGHT_PX;
  const apiWidth = printWidth ?? printableWidth;
  const rootRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  const { generate, loading } = usePrintable({
    filename,
    width: apiWidth,
    height: wide ? printableMinHeight : undefined,
    landscape: wide,
    rootWidthOverride: printWidth,
    testUuids,
    sessionId,
    queryFlags,
  });
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme("light");
  const isDark = colorScheme === "dark";

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const root = rootRef.current;
    if (!wrapper || !root) {
      return;
    }

    const wrapperObserver = new ResizeObserver(([entry]) => {
      setScale(Math.min(0.9, entry.contentRect.width / printableWidth));
    });

    const rootObserver = new ResizeObserver(([entry]) => {
      setContentHeight(entry.contentRect.height);
    });

    wrapperObserver.observe(wrapper);
    rootObserver.observe(root);
    return () => {
      wrapperObserver.disconnect();
      rootObserver.disconnect();
    };
  }, [printableWidth]);

  const landscapeFitScale = Math.min(
    A4_LANDSCAPE_PAGE_WIDTH_PX / printableWidth,
    A4_LANDSCAPE_PAGE_HEIGHT_PX / Math.max(contentHeight, printableMinHeight),
  );

  return (
    <PrintableContext.Provider value={{ prettier, wrapperRef }}>
      <Box
        ref={wrapperRef}
        bg={prettier ? (isDark ? "var(--mantine-color-dark-8)" : "#218BE6") : "transparent"}
        w="100%"
        pt="md"
      >
        {printButtonVisible && (
          <Affix className="no-print" position={{ bottom: 16, left: 32 }} zIndex={1000}>
            <Menu position="top-start" shadow="md" width={220}>
              <Menu.Target>
                <Button
                  disabled={loading}
                  variant="outline"
                  size="lg"
                  leftSection={<IconPrinter size={16} />}
                  rightSection={<IconChevronDown size={16} />}
                  color="#fff"
                  bg={isDark ? "var(--mantine-color-blue-8)" : "#218BE6"}
                >
                  {t("ResultList.print")}
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconPrinter size={16} />}
                  onClick={() => generate("print")}
                >
                  {t("ResultList.print")}
                </Menu.Item>
                <Menu.Item
                  leftSection={<IconDownload size={16} />}
                  onClick={() => generate("download")}
                >
                  {t("ResultList.pdf")}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Affix>
        )}
        <Box
          id="printable-scaled-wrapper"
          style={{
            transformOrigin: "top left",
            transform: `scale(${scale})`,
            marginLeft: `calc(max((100vw - ${scale * printableWidth}px), 0px) / 2)`,
            marginBottom: contentHeight > 0 ? `${-(contentHeight * (1 - scale))}px` : 0,
            paddingBottom: prettier ? 0 : "3rem",
          }}
        >
          <Box id={PRINTABLE_ROOT_ID} ref={rootRef} w={printableWidth} mih={printableMinHeight}>
            {children}
          </Box>
        </Box>
      </Box>
      {wide && (
        <style>{`
          @media print {
            @page { size: A4 landscape; margin: 0; }
            html, body {
              width: ${A4_LANDSCAPE_PAGE_WIDTH_PX}px !important;
              min-width: 0 !important;
            }
            #${PRINTABLE_ROOT_ID} { zoom: ${landscapeFitScale}; min-height: 0; }
          }
        `}</style>
      )}
    </PrintableContext.Provider>
  );
}

// ─── Assign & Export ──────────────────────────────────────────────────────────────────
export const Printable = Object.assign(Root, { Section, Divider });
